import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { PhoneConfirmationInput } from '../api/types';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { CancelNextChoice } from './CancelNextChoice';
import { type CancelNextValue, cancelNextComplete } from './cancelNext';
import { ConfirmationCall } from './ConfirmationCall';

// Ergebnis des Bestätigungsanrufs eintragen (OFFENE_FRAGEN B-29): bestätigt, nicht erreicht (Termin bleibt) oder abgesagt –
// bei Absage mit Grund und auf Wunsch „neuer Termin“ (Wiedervorlage für den Terminierer). Angerufen wird direkt von hier
// (über die Firmennummer, protokolliert, ohne Akquise-Sperre „Termin besteht“ – Thomas 13.10.2026).
export function PhoneConfirmationActions({ appointmentId, leadId, leadName }: { appointmentId: string; leadId: string | null; leadName: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [next, setNext] = useState<{ next?: CancelNextValue; later_date?: string }>({});
  const record = useMutation({
    mutationFn: (body: PhoneConfirmationInput) => papi.recordPhoneConfirmation(appointmentId, body),
    onSuccess: () => {
      setCancelOpen(false);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });
  return (
    <div className="phone-confirmation-actions">
      {leadId && can('call.start') && <ConfirmationCall appointmentId={appointmentId} leadId={leadId} />}
      <div className="form-actions">
        <button type="button" className="btn btn-small btn-primary" disabled={record.isPending} onClick={() => record.mutate({ outcome: 'CONFIRMED' })}>
          Bestätigt
        </button>
        <button type="button" className="btn btn-small" disabled={record.isPending} onClick={() => record.mutate({ outcome: 'NOT_REACHED' })}>
          Nicht erreicht
        </button>
        {can('appointment.cancel') && !cancelOpen && (
          <button type="button" className="btn btn-small" disabled={record.isPending} onClick={() => setCancelOpen(true)}>
            Abgesagt …
          </button>
        )}
      </div>
      {cancelOpen && (
        <form
          className="inline-panel"
          aria-label={`Absage eintragen: ${leadName}`}
          onSubmit={(e) => {
            e.preventDefault();
            record.mutate({ outcome: 'CANCELED', note: reason.trim(), ...next });
          }}
        >
          <label className="field wide">
            <span>Grund der Absage (Pflicht)</span>
            <textarea rows={2} required maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <CancelNextChoice value={next.next} laterDate={next.later_date} onChange={(n, later) => setNext({ next: n, later_date: later })} />
          <p className="muted">Der Makler wird über App, E-Mail und Kalender informiert; zusätzlich entsteht die Aufgabe, ihn persönlich anzurufen.</p>
          <div className="form-actions">
            <button type="submit" className="btn btn-danger" disabled={record.isPending || !cancelNextComplete(next.next, next.later_date)}>
              Absage eintragen
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setCancelOpen(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      {record.isError && <MutationError error={record.error} />}
    </div>
  );
}
